import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Eye,
  Shield,
  TrendingUp,
  MessageSquare,
  Search,
  BarChart,
  Target,
  Zap,
  Bot,
  CheckCircle,
  ArrowRight,
  Star,
  Award,
} from "lucide-react";

const Services = () => {
  const mainServices = [
    {
      id: "reputation",
      icon: Shield,
      title: "Reputation Management",
      price: "$200/month",
      description: "Complete monitoring and management of your online reputation across all major platforms.",
      features: [
        "24/7 AI-powered review monitoring",
        "Multi-platform coverage (Google, Yelp, Avvo, etc.)",
        "Automated review response assistance",
        "Monthly reputation reports",
        "Negative content suppression strategies",
        "Review generation campaigns",
        "Dedicated account manager",
      ],
      platforms: ["Google", "Yelp", "Avvo", "Vitals", "WebMD", "Zillow", "Facebook", "BBB"],
    },
    {
      id: "marketing",
      icon: Target,
      title: "Online Marketing Package",
      price: "$500/month",
      description: "Full-service digital marketing with AI-powered optimization to maximize your online visibility and convert searches into customers.",
      features: [
        "Everything in Reputation Management",
        "Google Platinum Partner services",
        "AdSense optimization & management",
        "AI-powered A/B testing (5000+ parameters)",
        "Search result influencing",
        "Positive content creation & promotion",
        "Competitor analysis",
        "Custom marketing strategy",
        "Weekly performance reports",
      ],
      platforms: ["Google Ads", "Facebook Ads", "LinkedIn", "SEO", "Content Marketing"],
      requiresConsultation: true,
    },
  ];

  const additionalServices = [
    {
      icon: Eye,
      title: "Review Monitoring",
      description: "Real-time alerts when new reviews are posted about your business on any platform.",
    },
    {
      icon: MessageSquare,
      title: "Response Management",
      description: "Professional responses to reviews that protect your brand while addressing concerns.",
    },
    {
      icon: TrendingUp,
      title: "Review Generation",
      description: "Automated campaigns to encourage satisfied customers to leave positive reviews.",
    },
    {
      icon: Search,
      title: "Search Optimization",
      description: "Make sure positive content ranks higher than negative mentions in search results.",
    },
    {
      icon: BarChart,
      title: "Analytics & Reporting",
      description: "Comprehensive dashboards showing your reputation score and trends over time.",
    },
    {
      icon: Zap,
      title: "Crisis Management",
      description: "Rapid response protocols for reputation emergencies and negative viral content.",
    },
  ];

  const platforms = [
    { name: "Google Reviews", description: "The most important platform for local businesses" },
    { name: "Yelp", description: "Critical for restaurants, retail, and service businesses" },
    { name: "Avvo", description: "Essential for attorneys and legal professionals" },
    { name: "Vitals", description: "Key platform for healthcare providers" },
    { name: "WebMD", description: "Important for doctors and medical practices" },
    { name: "Zillow", description: "Must-have for real estate professionals" },
    { name: "Facebook", description: "Major review platform with social reach" },
    { name: "BBB", description: "Trust indicator for all business types" },
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="hero-gradient py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="h-4 w-4" />
              Our Services
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Protect & Enhance Your{" "}
              <span className="gradient-text">Online Reputation</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              From review monitoring to full-service digital marketing, we have solutions 
              to fit your reputation management needs.
            </p>
          </div>
        </div>
      </section>

      {/* Main Service Packages */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Choose Your Plan
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Two comprehensive packages designed to protect and grow your online presence.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <Card
                key={service.id}
                id={service.id}
                data-testid={`service-card-${index}`}
                className={`bg-card border-border overflow-hidden ${
                  service.requiresConsultation ? "ring-2 ring-[#F37021]" : ""
                }`}
              >
                {service.requiresConsultation && (
                  <div className="bg-[#F37021] text-white text-center py-2 text-sm font-medium">
                    Most Popular - Consultation Required
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#F37021]/10 flex items-center justify-center">
                      <service.icon className="h-7 w-7 text-[#F37021]" />
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-foreground">{service.price}</p>
                      <p className="text-sm text-muted-foreground">per month</p>
                    </div>
                  </div>

                  <h3
                    className="text-2xl font-bold text-foreground mb-3"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground mb-6">{service.description}</p>

                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-[#F37021] flex-shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-3">Platforms covered:</p>
                    <div className="flex flex-wrap gap-2">
                      {service.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="text-xs bg-muted px-3 py-1 rounded-full"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Link
                    to={service.requiresConsultation ? "/book-consultation" : `/checkout/${service.id}`}
                  >
                    <Button
                      data-testid={`service-cta-${index}`}
                      className="w-full bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full py-6 text-lg"
                    >
                      {service.requiresConsultation ? "Book Consultation" : "Get Started"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI-Powered Advantage */}
      <section id="monitoring" className="py-20 lg:py-28 section-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Bot className="h-4 w-4" />
                AI-Powered
              </div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                The 5 Star Marketing Advantage
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our AI agents deploy advanced monitoring and response capabilities that 
                traditional reputation companies simply can't match.
              </p>
              <ul className="space-y-4">
                {[
                  "AI agents monitor reviews 24/7 across 50+ platforms",
                  "A/B testing with 5000+ parameters for optimal results",
                  "Machine learning identifies sentiment trends before they become problems",
                  "Automated positive review request campaigns",
                  "Smart response suggestions tailored to your brand voice",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#F37021] flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <Award className="h-12 w-12 text-[#F37021]" />
                <div>
                  <p className="font-bold text-foreground text-xl">Google Platinum Partner</p>
                  <p className="text-muted-foreground">Certified expertise in Google marketing</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                  <span className="text-foreground">A/B Test Parameters</span>
                  <span className="font-bold text-[#F37021]">5,000+</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                  <span className="text-foreground">Platforms Monitored</span>
                  <span className="font-bold text-[#F37021]">50+</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                  <span className="text-foreground">AI Agent Response Time</span>
                  <span className="font-bold text-[#F37021]">&lt;5 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              What's Included
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive reputation management features in every plan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card
                key={service.title}
                data-testid={`additional-service-${index}`}
                className="card-hover bg-card border-border"
              >
                <CardContent className="p-6">
                  <div className="feature-icon w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6" />
                  </div>
                  <h3
                    className="text-lg font-bold text-foreground mb-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms We Cover */}
      <section id="adsense" className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Platforms We Cover
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Comprehensive monitoring across all major review platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, index) => (
              <div
                key={platform.name}
                className="bg-white/5 rounded-xl p-6 hover:bg-white/10 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-[#F37021]/20 flex items-center justify-center mb-4">
                  <Star className="h-6 w-6 text-[#F37021]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{platform.name}</h3>
                <p className="text-sm text-gray-400">{platform.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#F37021] to-[#D95F18]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
            Get a free reputation analysis and see how we can help protect your online presence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="services-cta-btn"
                size="lg"
                className="bg-white text-[#F37021] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
              >
                Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-10 py-6 text-lg font-medium text-white border-white/30 hover:bg-white/10"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
